/**
 * compact-rail — общий механизм «узкая боковая панель → 2 кнопки + попапы».
 * docs/specs/board-compact-rail.md. Вынесен из board-screen (шаг 1 рефактора
 * 2026-05-29) как ПЕРЕИСПОЛЬЗУЕМЫЙ primitive для ВСЕХ экранов с боковой панелью
 * (board / daily / result / replay): owner-directive «функции в модуле, обязательные
 * для всех экранов, применяю паттерны».
 *
 * Что делает: когда панель `--rail-w` слишком узкая, чтобы показать лейблы действий
 * читаемо (ИЗМЕРЯЕМЫЙ триггер — весь лейбл при floor-шрифте `FS_BUTTON_MIN_PX` не
 * влезает в ширину панели; без подобранных порогов), вешает на панель класс
 * `compactClass` (его CSS прячет обычную обвязку и показывает 2 кнопки-триггера ⓘ/☰)
 * и монтирует попапы. Доска/контент НЕ уменьшаются.
 *
 * Generic: контент попапов (ⓘ-текст, ☰-действия) и реальные кнопки действий (для
 * замера лейблов) даёт экран. Сама механика (замер, попапы, тоггл, пересчёт на
 * resize/geometry/fonts) — здесь.
 */

import { mountRailPopover, type RailPopoverHandle } from '../components/rail-popover.js';

import { textWidthPx } from './autofit-text.js';
import { t } from './i18n/index.js';
import { FS_BUTTON_MIN_PX, VIEWPORT_APPLIED_EVENT } from './viewport-sizer.js';

export interface CompactRailOptions {
  /** Панель, на которую вешается `compactClass` при сужении. */
  readonly rail: HTMLElement;
  /** Контейнер, куда монтируются 2 кнопки-триггера (ⓘ/☰). */
  readonly compactHost: HTMLElement;
  /**
   * Реальные кнопки действий — для ИЗМЕРЯЕМОГО триггера (их лейблы меряются; первая
   * служит гарнитурой-эталоном — работает даже если кнопка `display:none`).
   */
  readonly actionButtons: readonly HTMLElement[];
  /** Класс компакта, который тогглится на `rail` (CSS экрана прячет/показывает). */
  readonly compactClass: string;
  /** Заголовок ⓘ-попапа. */
  readonly infoTitle: string;
  /** Тело ⓘ-попапа — строится при каждом открытии (живые данные актуальны). */
  readonly buildInfoBody: () => HTMLElement;
  /** Тело ☰-попапа (`close` — закрыть попап после действия). */
  readonly buildActionsBody: (close: () => void) => HTMLElement;
  /**
   * Floor-шрифт для ИЗМЕРЯЕМОГО триггера (px) — минимальный РЕАЛЬНО ОТОБРАЖАЕМЫЙ
   * размер лейбла, ниже которого панель сворачивается в компакт. По умолчанию
   * `FS_BUTTON_MIN_PX` (дизайн-floor читаемости из стайлшита).
   *
   * Дельта-рассинхрон триггера (fix 2026-05-29): отображаемый размер кнопки =
   * fit × fillRatio (дельта). Триггер замеряет ширину при floor-шрифте — но если
   * замерять при `FS_BUTTON_MIN_PX`, то ОТОБРАЖАЕМЫЙ (delta-масштабированный) текст
   * мог опускаться до `FS_BUTTON_MIN_PX × fillRatio` (≈9px при 0.7) БЕЗ срабатывания
   * компакта. Чтобы отображаемый текст НИКОГДА не был ниже floor читаемости,
   * замерять надо при `FS_BUTTON_MIN_PX / fillRatio`. Этот параметр передаёт уже
   * скорректированный «эффективный floor» (attachActionRail вычисляет его из общей
   * дельты), так что замер тут идёт напрямую при нём.
   */
  readonly floorPx?: number;
}

export interface CompactRailHandle {
  detach(): void;
}

/**
 * Привязать compact-rail к панели экрана. `root` — корень экрана (наблюдается
 * ResizeObserver'ом). Возвращает handle с detach() для unmount экрана.
 */
export function attachCompactRail(root: HTMLElement, opts: CompactRailOptions): CompactRailHandle {
  const { rail, compactHost, actionButtons, compactClass } = opts;

  // ⓘ — описательный текст (строится при открытии → живые данные).
  const infoPop: RailPopoverHandle = mountRailPopover({
    triggerHost: compactHost,
    icon: 'ⓘ',
    ariaLabel: t('game.compactRail.infoAria'),
    title: opts.infoTitle,
    triggerClassName: 'rail-compact-btn--info',
    buildBody: () => opts.buildInfoBody(),
  });

  // ☰ — действия полными лейблами.
  // eslint-disable-next-line prefer-const -- closeActions ссылается на actionsPop (циклическая ссылка через замыкание)
  let actionsPop: RailPopoverHandle;
  const closeActions = (): void => actionsPop.close();
  actionsPop = mountRailPopover({
    triggerHost: compactHost,
    icon: '☰',
    ariaLabel: t('game.compactRail.actionsAria'),
    title: t('game.compactRail.actionsAria'),
    triggerClassName: 'rail-compact-btn--actions',
    buildBody: () => opts.buildActionsBody(closeActions),
  });

  // ИЗМЕРЯЕМЫЙ триггер: весь лейбл в одну строку при ЭФФЕКТИВНОМ floor-шрифте кнопки
  // не влезает в доступную ширину панели (--rail-w − 2·--m, отступ кнопки = --m) →
  // компакт. floor по умолчанию = FS_BUTTON_MIN_PX; attachActionRail передаёт
  // скорректированный `FS_BUTTON_MIN_PX / fillRatio`, чтобы ОТОБРАЖАЕМЫЙ delta-
  // масштабированный текст никогда не опускался ниже floor читаемости.
  // Только в боковой раскладке (rail). Гарнитура-эталон — первая кнопка действий.
  const floorPx = opts.floorPx ?? FS_BUTTON_MIN_PX;
  const fontRef = actionButtons[0] ?? rail;
  const update = (): void => {
    const docEl = document.documentElement;
    const isRail = docEl.dataset.layout === 'rail';
    let needCompact = false;
    if (isRail) {
      const cs = getComputedStyle(docEl);
      const geomRailW = parseFloat(cs.getPropertyValue('--rail-w')) || 0;
      const m = parseFloat(cs.getPropertyValue('--m')) || 0;
      // boardW считаем ИЗ --cell-size (JS-px, engine-identical) — та же формула, что
      // viewport-sizer.computeRailW. НЕЛЬЗЯ читать --board-w через getComputedStyle:
      // это CSS-calc-токен, Chrome резолвит в px, а Firefox/WebKit возвращают строку
      // «calc(…)» → NaN → 0 → защита «панель ≤ поле» падает → наезд (parity-гейт ловил).
      const cell = parseFloat(cs.getPropertyValue('--cell-size')) || 0;
      const gapB = Math.max(1, Math.round(cell * 0.08));
      const fpad = Math.max(4, Math.round(cell * 0.28));
      const boardW = 9 * cell + 8 * gapB + 2 * fpad;

      // Самый длинный лейбл действия при floor-шрифте (МЕРЯЕМ, не угадываем) → ширина
      // панели, чтобы он влез: label + 2m (панель-паддинг, см. avail ниже) + m воздух.
      let longest = 0;
      for (const btn of actionButtons) {
        longest = Math.max(longest, textWidthPx(btn.textContent ?? '', floorPx, fontRef));
      }
      const needed = longest + 3 * m;

      // A (owner-decision 2026-06-05): панель РАСШИРЯЕТСЯ в свободное боковое поле,
      // чтобы полные кнопки влезали, вместо схлопывания. Поле центрированной доски:
      //   flex = (vw − board)/2 − 2m  (панель может занять его, доска НЕ двигается —
      //   она центрируется отдельно, панель .postament-rail = position:absolute).
      // Расширяем до needed, но не больше flex (иначе наедет на доску) и не меньше
      // геометрического дефолта 5·cell. Override — ЛОКАЛЬНЫЙ на панели (её width:
      // var(--rail-w)); :root не трогаем (им владеет viewport-sizer). Компакт — только
      // если даже всё поле не вмещает самый длинный лейбл (настоящий последний resort).
      const flex = (docEl.clientWidth - boardW) / 2 - 2 * m;
      const effective = Math.max(geomRailW, Math.min(needed, Math.max(0, flex)));
      if (effective > geomRailW + 0.5) {
        rail.style.setProperty('--rail-w', `${Math.round(effective)}px`);
      } else {
        rail.style.removeProperty('--rail-w');
      }

      const avail = effective - 2 * m;
      if (avail > 0) {
        for (const btn of actionButtons) {
          if (textWidthPx(btn.textContent ?? '', floorPx, fontRef) > avail) {
            needCompact = true;
            break;
          }
        }
      }
    } else {
      // stacked — дефолтная геометрия (:root), локальный override снимаем.
      rail.style.removeProperty('--rail-w');
    }
    rail.classList.toggle(compactClass, needCompact);
    compactHost.setAttribute('aria-hidden', needCompact ? 'false' : 'true');
    if (!needCompact) {
      infoPop.close();
      actionsPop.close();
    }
  };

  // rAF-дебаунс + trailing-замер после переверстки (смена раскладки не мгновенна).
  let raf = 0;
  let trailing = 0;
  const run = (): void => {
    raf = 0;
    update();
  };
  const schedule = (): void => {
    if (raf) return;
    raf = requestAnimationFrame(run);
  };
  const scheduleSettled = (): void => {
    schedule();
    if (trailing) clearTimeout(trailing);
    trailing = window.setTimeout(schedule, 160);
  };

  scheduleSettled();
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(scheduleSettled);
    ro.observe(root);
  }
  window.addEventListener(VIEWPORT_APPLIED_EVENT, scheduleSettled);
  if (typeof document !== 'undefined' && 'fonts' in document) {
    void document.fonts.ready.then(scheduleSettled);
  }

  return {
    detach(): void {
      if (raf) cancelAnimationFrame(raf);
      if (trailing) clearTimeout(trailing);
      if (ro) ro.disconnect();
      window.removeEventListener(VIEWPORT_APPLIED_EVENT, scheduleSettled);
      rail.style.removeProperty('--rail-w'); // снять локальный override ширины панели
      infoPop.unmount();
      actionsPop.unmount();
    },
  };
}
